<script setup lang="ts">
// Props optionnelles pour personnaliser le composant
interface Props {
  phoneNumber?: string;
  message?: string;
  size?: 'sm' | 'md' | 'lg';
  position?: 'bottom-left' | 'bottom-right';
}

const props = withDefaults(defineProps<Props>(), {
  phoneNumber: '',
  message: 'Bonjour, j\'aimerais obtenir plus d\'informations',
  size: 'md',
  position: 'bottom-left'
});

// Générer le lien WhatsApp
const whatsappUrl = computed(() => {
  const baseUrl = 'https://wa.me/';
  const encodedMessage = encodeURIComponent(props.message);
  return `${baseUrl}${props.phoneNumber}?text=${encodedMessage}`;
});

// Classes dynamiques selon la taille
const sizeClasses = computed(() => {
  const sizes = {
    'sm': 'w-12 h-12',
    'md': 'w-16 h-16',
    'lg': 'w-20 h-20'
  };
  return sizes[props.size];
});

// Classes dynamiques selon la position
const positionClasses = computed(() => {
  const positions = {
    'bottom-left': 'bottom-6 left-6',
    'bottom-right': 'bottom-6 right-6'
  };
  return positions[props.position];
});

// Taille de l'icône SVG selon la taille du bouton
const iconSize = computed(() => {
  const sizes = {
    'sm': 'w-6 h-6',
    'md': 'w-8 h-8',
    'lg': 'w-10 h-10'
  };
  return sizes[props.size];
});
</script>

<template>
  <div :class="['whatsapp-floating fixed z-50 ',
        positionClasses
  ]">
    <a
        :href="whatsappUrl"
        target="_blank"
        rel="noopener noreferrer"
        :class="[
        'flex items-center justify-center rounded-full shadow-lg transition-all duration-300 hover:scale-110 hover:shadow-xl',
        sizeClasses
      ]"
        class="whatsapp-button"
    >
      <div class="whatsapp-bg"></div>
      <svg
          :class="iconSize"
          class="text-white relative z-10"
          fill="currentColor"
          viewBox="0 0 24 24"
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.488"/>
      </svg>
    </a>
  </div>
</template>

<style scoped>
.whatsapp-floating {
  pointer-events: none;
}

.whatsapp-button {
  pointer-events: auto;
  position: relative;
  overflow: hidden;
}

.whatsapp-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  border-radius: 50%;
  animation: bounce 2s infinite ease-in-out;
}

.whatsapp-button::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  background: rgba(37, 211, 102, 0.3);
  border-radius: 50%;
  transform: translate(-50%, -50%) scale(1);
  animation: pulse 2s infinite ease-out;
}

/* Animation de rebond continu */
@keyframes bounce {
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-8px) scale(1.05);
  }
}

/* Animation de pulsation pour l'effet d'onde */
@keyframes pulse {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.7;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 0.3;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.4);
    opacity: 0;
  }
}

/* Effet hover amélioré */
.whatsapp-button:hover {
  transform: scale(1.1);
}

.whatsapp-button:hover .whatsapp-bg {
  animation-play-state: paused;
  background: linear-gradient(135deg, #128c7e 0%, #25d366 100%);
}

/* Animation d'entrée au chargement */
.whatsapp-floating {
  animation: slideIn 0.8s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateY(100px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .whatsapp-button {
    bottom: 20px !important;
    left: 20px !important;
  }
}
</style>